import { IPubSubAPI } from "@API/pubsub";
import { ZenObservable } from '../../node_modules/zen-observable-ts'
import { v4 } from "uuid";
import Utils from "@utils/index";
import { first } from "lodash";


class PTZController {
  private _videoStream: VideoStream | null = null;
  private _publisher: IPubSubAPI | null = null;
  private _observable: Function | null = null;
  private _connections: Map<string, ZenObservable.Subscription | undefined> = new Map();
  private _queue: { topic: string, message: any }[] = [];
  private _edgeDevice: string = process.env.REACT_APP_STACK === 'prod' ? 'a8_a1_59_da_31_b1' : 'a8_a1_59_9e_d4_54';

  constructor (videoStream: VideoStream, publisher: IPubSubAPI, observable: Function) {
    this._videoStream = videoStream;
    this._publisher = publisher;
    this._observable = observable;

    if (videoStream.iotThingName) {
      if (videoStream.iotThingName !== "DETACHED" && process.env.REACT_APP_STACK === 'prod') {
        this._edgeDevice = videoStream.iotThingName;
      }
    }
  }

  public processInitialRequest(): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            speed: 50
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public connect(): PTZController {
    return this;
  }

  public disconnect(): void {
    this._connections?.forEach(c => c && c.unsubscribe());
  }

  public getVideoStream(): VideoStream | null {
    return this._videoStream;
  }

  public getPublisher(): IPubSubAPI | null {
    return this._publisher;
  }

  private pushToQueue(topic: string, message: any): void {
    this._queue.push({ topic, message })
  }

  private processNext(): void {
    const f = first(this._queue);
    if (f) {
      this._publisher?.publishOnTopic(f.topic, f.message)
      this._queue.shift();
    }
  }

  public pan(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "rpan": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public tilt(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "rtilt": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public center(value: number[], imagewidth: number, imageheight: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "center": value,
            "imagewidth": imagewidth,
            "imageheight": imageheight
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public areazoom(value: number[], imagewidth: number, imageheight: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "areazoom": value,
            "imagewidth": imagewidth,
            "imageheight": imageheight
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public rzoom(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "rzoom": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public rpan(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "rpan": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public rtilt(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "rtilt": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public speed(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "speed": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public zoom(value: number): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "zoom": value
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public setHome(): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "presetName": "Home",
            "setHome": true,
            speed: 50
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  public goToHome(): void {
    if (this._publisher) {
      this.pushToQueue(
        this.getRequestTopic(),
        {
          ...this.getRequestHeaders(),
          parameters: {
            "presetPosition": "Home",
            speed: 50
          }
        }
      )
      if (this._queue.length === 1) this.processNext();
    }
  }

  private onMessage(message: object): void {
    this.disconnect();
    if (this._queue.length > 0) this.processNext();
    if (this._observable) this._observable(message);
  }

  private getRequestTopic(): string {
    const uniqueId: string = v4();
    const topic: string = `edge/${this._edgeDevice}/http/requests/${uniqueId}`
    const obs = this._publisher?.subscribeToTopic(topic.replace('/requests/', '/responses/'), this.onMessage.bind(this));
    this._connections.set(topic, obs);

    return topic;
  }

  private getRequestHeaders(): object {
    return {
      "device": {
        "manufacturer": this._videoStream?.manufacturer || "", // camera manufacturer (should be in the streams table)
        "type": this._videoStream?.type || "PTZ", // camera type (should be in the streams table)
        "url": Utils.RegEx.findIPAddress(this._videoStream?.ptz?.mjpegUrl || "")
      },
      "action": "ptz/control",
    }
  }
}

export default PTZController;
